import React, { useEffect, useMemo, useRef } from "react";
import type { StageDTO } from "../lib/api";
import { dayLabels } from "../lib/format";
import { IconX } from "./icons";

export type BrowseFilter = {
  days: number[];
  stages: string[];
  genres: string[];
};

export const EMPTY_FILTER: BrowseFilter = { days: [], stages: [], genres: [] };

export function FilterSheet({
  open,
  stages,
  genres,
  value,
  eventStartDate,
  eventDays,
  onChange,
  onClose,
}: {
  open: boolean;
  stages: StageDTO[];
  genres: string[];
  value: BrowseFilter;
  /** Parent event's startDate so day chips read e.g. "Fri · Jul 17". */
  eventStartDate?: string | null;
  /** How many day chips to render — defaults to 3 when unknown. */
  eventDays?: number;
  onChange: (next: BrowseFilter) => void;
  onClose: () => void;
}) {
  const lastFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    lastFocused.current = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      lastFocused.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const totalSelected = value.days.length + value.stages.length + value.genres.length;

  function toggle<T>(arr: T[], v: T): T[] {
    return arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];
  }

  return (
    <div
      className="sheet-backdrop"
      onClick={e => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Filter lineup"
      data-testid="filter-sheet"
    >
      <div className="sheet filter-sheet">
        <div className="sheet-handle" />
        <div className="filter-head">
          <div>
            <span className="sub">REFINE</span>
            <h2 style={{ margin: "2px 0 0" }}>Filter lineup</h2>
            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                letterSpacing: "0.04em",
                color: "var(--ink-faint)",
              }}
            >
              Pick several — matches any of the chosen values.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="chip"
              onClick={() => onChange(EMPTY_FILTER)}
              disabled={totalSelected === 0}
              style={{ opacity: totalSelected === 0 ? 0.4 : 1 }}
              data-testid="filter-clear-all"
            >
              Clear all
            </button>
            <button className="chip" onClick={onClose} aria-label="Close" data-testid="filter-close">
              <IconX />
            </button>
          </div>
        </div>

        <div className="filter-body">
          <FilterSection title="Days" selectedCount={value.days.length}>
            <div className="filter-grid">
              <FilterPill
                pressed={value.days.length === 0}
                onClick={() => onChange({ ...value, days: [] })}
                data-testid="filter-day-all"
              >
                All days
              </FilterPill>
              {(eventDays && eventDays > 0 ? eventDays : 3) &&
                Array.from(
                  { length: eventDays && eventDays > 0 ? eventDays : 3 },
                  (_, i) => i + 1,
                ).map(d => (
                  <FilterPill
                    key={d}
                    pressed={value.days.includes(d)}
                    onClick={() => onChange({ ...value, days: toggle(value.days, d) })}
                    data-testid={`filter-day-${d}`}
                  >
                    {dayLabelFor(d, eventStartDate)}
                  </FilterPill>
                ))}
            </div>
          </FilterSection>

          <FilterSection title="Stages" selectedCount={value.stages.length}>
            <div className="filter-grid">
              <FilterPill
                pressed={value.stages.length === 0}
                onClick={() => onChange({ ...value, stages: [] })}
                data-testid="filter-stage-all"
              >
                All stages
              </FilterPill>
              {stages.map(s => {
                const on = value.stages.includes(s.slug);
                return (
                  <FilterPill
                    key={s.slug}
                    pressed={on}
                    onClick={() => onChange({ ...value, stages: toggle(value.stages, s.slug) })}
                    data-testid={`filter-stage-${s.slug}`}
                    style={{
                      borderColor: on ? s.color ?? undefined : undefined,
                      color: on ? s.color ?? undefined : undefined,
                      boxShadow: on ? `0 0 0 1px ${s.color}30, 0 0 12px ${s.color}40 inset` : undefined,
                    }}
                  >
                    {s.name}
                  </FilterPill>
                );
              })}
            </div>
          </FilterSection>

          {genres.length > 0 && (
            <FilterSection title="Genres" selectedCount={value.genres.length}>
              <div className="filter-grid">
                <FilterPill
                  pressed={value.genres.length === 0}
                  onClick={() => onChange({ ...value, genres: [] })}
                  data-testid="filter-genre-all"
                >
                  All genres
                </FilterPill>
                {genres.map(g => (
                  <FilterPill
                    key={g}
                    pressed={value.genres.includes(g)}
                    onClick={() =>
                      onChange({ ...value, genres: toggle(value.genres, g) })
                    }
                    data-testid={`filter-genre-${g}`}
                  >
                    {g}
                  </FilterPill>
                ))}
              </div>
            </FilterSection>
          )}
        </div>

        <div className="filter-foot">
          <button className="filter-done" onClick={onClose} data-testid="filter-done">
            {totalSelected
              ? `Show matches (${totalSelected} filter${totalSelected === 1 ? "" : "s"})`
              : "Done"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FilterSection({
  title,
  selectedCount,
  children,
}: {
  title: string;
  selectedCount: number;
  children: React.ReactNode;
}) {
  return (
    <section className="filter-section">
      <h3>
        {title}
        {selectedCount > 0 && (
          <span
            style={{
              marginLeft: 6,
              color: "var(--amber-hot)",
              fontFamily: "var(--fs-mono)",
              fontSize: 10,
              letterSpacing: "0.12em",
            }}
          >
            · {selectedCount} selected
          </span>
        )}
      </h3>
      {children}
    </section>
  );
}

function FilterPill({
  pressed,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { pressed: boolean }) {
  return (
    <button
      role="checkbox"
      aria-checked={pressed}
      aria-pressed={pressed}
      className={`filter-pill ${pressed ? "on" : ""}`}
      {...rest}
    >
      {children}
    </button>
  );
}

/** @deprecated kept for Browse callers that still pass no startDate. */
export function dayLabel(d: number): string {
  return `Day ${d}`;
}

function dayLabelFor(d: number, startDateIso: string | null | undefined): string {
  if (!startDateIso) return `Day ${d}`;
  const labels = dayLabels(startDateIso, 14);
  return labels[d - 1] ?? `Day ${d}`;
}
